import { CSSProperties, useEffect, useState } from "react";
import styled from "styled-components";
import media from "styled-media-query";

import { Text, ColorV2 } from "@adoptaunabuelo/react-components";
import { Cake, Check, Flower, Heart, Mail, Rocket, Star } from "lucide-react";
import ActivitiesList from "../../../components/list/ActivitiesList";
import PressLogo from "../../../assets/logo/Press";
import TestimonyList from "../../../components/list/TestimonyList";
import ReliefModal from "../../../components/modal/ReliefModal";
import { useSelector } from "react-redux";
import { State } from "../../../redux";
import ProtectedDonation from "../../../components/modal/ProtectedDonation";

const screenSize1: any = "1350px";
const screenSize2: any = "1024px";
const screenSize3: any = "320px";

const Container = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
`;
const Section = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	padding: 64px 0px;
	${media.lessThan("medium")`
        padding: 48px 0px;
    `}
`;
const SectionBackground = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	padding: 64px;
	background-color: ${ColorV2.surface.background};
	border-radius: 16px;
	${media.lessThan("medium")`
        padding: 64px 24px;
		margin: 0px -24px;
    `}
`;
const SectionFinal = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	padding: 0px 10%;
	justify-content: center;
	align-items: center;
	height: calc(var(--doc-height) - var(--nav-bar-height));
	${media.lessThan(screenSize1)`
        padding: 0px 5%;
    `}
	${media.lessThan(screenSize2)`
        padding: 0px 48px;
    `}
    ${media.lessThan("medium")`
        padding: 64px 0px;
    `}
    ${media.lessThan("small")`
        padding: 48px 0px;
    `}
`;
const ProgressContainer = styled.div`
	display: flex;
	${media.lessThan("medium")`
        flex-direction: column;
        gap: 32px;
	`}
`;
const ProgressView = styled.div`
	display: flex;
	flex-direction: column;
	${media.lessThan("medium")`
        
	`}
`;
const Col = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
`;
const Row = styled.div`
	display: flex;
	flex: 1;
	flex-direction: row;
`;
const BigNumber = styled(Text)`
	font-weight: 800 !important;
	font-size: 115px !important;
	line-height: 115px !important;
	${media.lessThan(screenSize1)`
        font-size: 100px !important;
        line-height: 100px !important;
    `}
	${media.lessThan(screenSize2)`
        font-size: 80px !important;
        line-height: 80px !important;
    `}
	${media.lessThan("small")`
        font-size: 60px !important;
        line-height: 60px !important;
	`}
	${media.lessThan(screenSize3)`
        font-size: 52px !important;
        line-height: 52px !important;
    `}
`;
const Cell = styled.div`
	display: flex;
	flex-direction: row;
	margin-bottom: 12px;
`;
const CheckView = styled.div`
	display: flex;
	height: 22px;
	width: 22px;
	align-items: center;
	justify-content: center;
	margin-right: 12px;
`;
const PieChartCol = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 32px;
	${media.lessThan("medium")`
        width: -webkit-fill-available;
        width: -moz-available;
	`}
`;
const PressRow = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	${media.lessThan("medium")`
        flex-wrap: wrap;
        gap: 24px;
    `}
`;
const PriceView = styled.div`
	display: flex;
	flex-direction: row;
	padding: 24px;
	gap: 16px;
	align-items: center;
	${media.lessThan("medium")`
		flex-direction: column;
		align-items: flex-start;
		gap: 0px;
		flex: 1;
	`}
`;
const PriceRow = styled.div`
	display: flex;
	flex-direction: row;
	width: fit-content;
	border-radius: 16px;
	overflow: hidden;
	margin-bottom: 48px;
	background-color: white;
	${media.lessThan("medium")`
		width: 100%;
	`}
`;
const Icon = styled.img`
	height: 64px;
	width: 64px;
	margin-bottom: 16px;
`;
const DataImage = styled.img`
	display: flex;
	flex: 1;
	object-fit: cover;
	width: 100%;
	height: 100%;
	border-radius: 24px;
`;

const Info = (props: Props) => {
	const appLocation = useSelector((state: State) => state.location.location);
	const isMobile = useSelector((state: State) => state.screen.isMobile);
	const [showRelief, setShowRelief] = useState(false);
	const [priceAmount, setPriceAmount] = useState("0");

	useEffect(() => {
		getReliefAmount(props.price);
	}, [props.price]);

	const getReliefAmount = (price: number) => {
		const reliefSection1 = price > 250 ? 250 : price;
		const reliefSection2 = price > 250 ? price - 250 : 0;
		const relief = reliefSection1 * 0.8 + reliefSection2 * 0.4;
		setPriceAmount(relief.toFixed(2));
	};

	const chartOptions = [
		{
			title: "Actividades con personas mayores",
			value: 90,
			color: ColorV2.text.primary,
			textColor: ColorV2.text.primary,
			fontSize: 120,
			bullets: [
				"Los mayores no pagan nada por participar.",
				"Acompañamiento semanal.",
				"Celebramos su cumpleaños.",
				"Asistencia a nuestras actividades.",
				"Cumplimos sus sueños.",
				"Asistencia psicológica en casos de depresión.",
			],
		},
		{
			title: "Costes operacionales",
			value: 8,
			color: ColorV2.text.neutralHard,
			textColor: ColorV2.text.neutralHard,
			fontSize: 48,
			bullets: [
				"Nos permiten funcionar como organización (alquiler de oficina y suministros).",
				"Impuestos.",
				"Inversión en tecnología para llegar a todos los abuelos del mundo.",
			],
		},
		{
			title: "Comisiones bancarias",
			value: 2,
			color: ColorV2.text.neutralMedium,
			textColor: ColorV2.text.neutralMedium,
			fontSize: 32,
		},
	];

	const activities = [
		{
			name: "Ramón",
			info: "Cumplimos el sueño de mayores como Ramón. Su sueño era volver a jugar al béisbol. Gracias a vuestra ayuda, Ramón pudo disfrutar de un día inolvidable.",
			testimony:
				"No puedo creer todo lo que habéis hecho. Estoy como un niño pequeño.",
			img: require("../../../assets/images/history/ramon.webp"),
			video: "https://youtube.com/shorts/n72-NPDbAeY",
			label: {
				title: "Sueños",
				icon: (
					<Rocket
						height={20}
						width={20}
						color={ColorV2.text.neutralHard}
					/>
				),
			},
		},
		{
			name: "Felisa",
			info: "Todas las navidades llenamos las residencias de felicitaciones y conseguimos que miles de abuelos se sientan acompañados en estas fechas tan señaladas.",
			testimony:
				"Me parece muy bonito porque, sin conocernos, da la sensación de que están a nuestro lado y que nos conocen.",
			img: require("../../../assets/images/history/felisa.webp"),
			video: "https://www.youtube.com/watch?v=D--PsMjx6Ys",
			label: {
				title: "Cartas",
				icon: (
					<Mail
						height={20}
						width={20}
						color={ColorV2.text.neutralHard}
					/>
				),
			},
		},
		{
			name: "Loli",
			info: "Con vuestra ayuda, organizamos fiestas de cumpleaños para nuestros mayores, haciendo que su día sea inolvidable. ¡Tarta, velas, globos y muchos invitados!",
			testimony: "Les daría un abrazo a todos, porque sois maravillosos.",
			img: require("../../../assets/images/history/loli.webp"),
			video: "https://www.youtube.com/watch?v=9BoL3KmF97M&list=PL2bO3y1ZkJ312Ovt0hS_v3G6oZsPwQkkA",
			label: {
				title: "Cumpleaños",
				icon: (
					<Cake
						height={20}
						width={20}
						color={ColorV2.text.neutralHard}
					/>
				),
			},
		},
		{
			name: "Valentina",
			info: "Realizamos actividades en residencias, como Abuelos Hackers. Una actividad para reducir la brecha digital de las personas mayores y fomentar su autonomía, mejorando su autosuficiencia y ampliando sus opciones de socialización y ocio.",
			testimony:
				"Gracias a Adopta Un Abuelo ahora puedo hablar con mi nieto por el móvil todas las semanas.",
			img: require("../../../assets/images/history/valentina.webp"),
			video: "https://www.youtube.com/shorts/9fkKyzQHdOc",
			label: {
				title: "Actividades",
				icon: (
					<Flower
						height={20}
						width={20}
						color={ColorV2.text.neutralHard}
					/>
				),
			},
		},
		{
			name: "Raquel",
			info: "A través de nuestro programa original de voluntariado, acompañamos a personas mayores que lo necesitan. Y siempre resulta una experiencia enriquecedora tanto para abuelos como para voluntarios.",
			testimony:
				"Gracias al programa de Adopta Un Abuelo ya tengo una amistad más.",
			img: require("../../../assets/images/history/raquel.webp"),
			video: "https://youtube.com/shorts/4jzq8rOR8l0",
			label: {
				title: "Voluntariado",
				icon: (
					<Heart
						height={20}
						width={20}
						color={ColorV2.text.neutralHard}
					/>
				),
			},
		},
	];

	const testimonies = [
		{
			name: "María Flores",
			image: require("../../../assets/images/testimonies/maria.webp"),
			testimony:
				"Conocer a África, mi abuela adoptada, ha sido increíble. He podido aprender cada día de ella y me ha dado mucho cariño y amor. África siempre me dice que cómo podemos ser tan buenas amigas con todos los años que nos llevamos, y es que para la amistad no hay edad. Es una experiencia única que te llena a nivel personal.",
		},
		{
			name: "Luis Silvestre",
			image: require("../../../assets/images/testimonies/luis.webp"),
			testimony:
				"Lo recomiendo 100% por compartir experiencias con las personas más sabias del planeta y por lo gratificante que es dedicarles tiempo a nuestros mayores que se encuentren solos.",
		},
		{
			name: "Gaby Lopez",
			image: require("../../../assets/images/testimonies/gaby.webp"),
			testimony:
				"Recomendaría Adopta Un Abuelo sin ninguna duda. Es una bonita manera de devolver lo que España me ha brindado durante estos 12 años, qué mejor forma que hacerlo dando cariño y atención a nuestros mayores. Me encantan los momentos divertidos con mi abuela Pilar.",
		},
		{
			name: "Mayra Camarillo",
			image: require("../../../assets/images/testimonies/mayra.webp"),
			testimony:
				"He estado hoy con mi hija viviendo la experiencia y la verdad que ha sido muy entrañable, ver tantos jóvenes comprometidos con la causa de dar cariño desinteresadamente y alegrar la tarde compartiendo su tiempo para celebrar la vida de estas dulces abuelas… Una gran labor que sin duda alguna merece la pena apoyar.",
		},
	];

	const data = [
		{
			title: "+12.000",
			subtitle: "abuelos acompañados",
		},
		{
			title: "+66.000",
			subtitle: "horas de compañía",
		},
		{
			title: "+500",
			subtitle: "actividades",
		},
	];

	const renderProgressView = (item: any, border?: "right" | "bottom") => (
		<ProgressView
			style={{
				padding: isMobile
					? "0px"
					: border && border === "right"
					? "0px 32px 0px 0px"
					: border && border === "bottom"
					? "0px 0px 32px 32px"
					: "0px 0px 0px 32px",
				borderRight: isMobile
					? "0px"
					: border && border === "right"
					? "1px solid " + ColorV2.border.neutralSoft
					: "none",
				borderBottom: isMobile
					? "0px"
					: border && border === "bottom"
					? "1px solid " + ColorV2.border.neutralSoft
					: "none",
			}}
		>
			<Text
				type="p"
				style={{
					fontSize: item.fontSize,
					lineHeight: item.fontSize + "px",
					fontWeight: 800,
					color: item.textColor,
				}}
			>
				{item.value}%
			</Text>
			<Text
				type="h4"
				weight="medium"
				style={{ marginBottom: 24, color: item.textColor }}
			>
				{item.title}
			</Text>
			{item.bullets &&
				item.bullets.map((bulletItem: string, index: number) => (
					<Cell key={"progress-bullet-" + index}>
						<CheckView>
							<Check
								height={16}
								width={16}
								color={item.textColor}
							/>
						</CheckView>
						<Text type="p" style={{ flex: 1 }}>
							{bulletItem}
						</Text>
					</Cell>
				))}
		</ProgressView>
	);

	return (
		<Container style={props.style}>
			<ReliefModal
				isVisible={showRelief}
				price={15}
				charge="monthly"
				onClose={() => setShowRelief(false)}
			/>
			<Section
				style={{
					borderBottom: "1px solid " + ColorV2.border.neutralSoft,
				}}
			>
				<Text type="h2" weight="semibold" style={{ marginBottom: 32 }}>
					Por qué tu donación es importante
				</Text>
				<Text type="h6" style={{ marginBottom: isMobile ? 24 : 48 }}>
					La soledad no deseada provoca depresión, entre otras
					enfermedades. Cada día, en España,{" "}
					<span
						style={{
							padding: "0px 4px",
							backgroundColor: ColorV2.surface.secondary,
							fontWeight: 600,
						}}
					>
						3 personas mayores de 70 años
					</span>{" "}
					se quitan la vida. Tenemos la oportunidad de prevenirlo
					devolviéndoles el amor que nos han dado y recordándoles lo
					importantes que son.
				</Text>
				<BigNumber type="h3">3.000.000</BigNumber>
				<Text type="h3">de mayores en España viven solos</Text>
				<Text
					type="c2"
					style={{
						color: ColorV2.text.neutralMedium,
						marginTop: isMobile ? 32 : 96,
					}}
				>
					Los datos se han obtenido de la National Academies of
					Sciences, Engineering, and Medicine. 2020. Social Isolation
					and Loneliness in Older Adults: Opportunities for the Health
					Care System. Washington, DC: The National Academies Press.
				</Text>
			</Section>
			<Section
				style={{
					borderBottom: "1px solid " + ColorV2.border.neutralSoft,
				}}
			>
				<Text
					type="h2"
					weight="semibold"
					style={{ marginBottom: isMobile ? 8 : 24 }}
				>
					Con tu ayuda, acompañamos a personas mayores todos los días
				</Text>
				<Text type="h6" style={{ marginBottom: isMobile ? 32 : 64 }}>
					Estas son algunas de las historias que hacemos posible con
					tu donación
				</Text>
				<ActivitiesList testimonies={activities} />
			</Section>
			<Section>
				<Text type="h2" weight="semibold">
					Qué hacemos con tu donación
				</Text>
				<Text
					type="h6"
					style={{
						marginTop: 24,
						marginBottom: 24,
					}}
				>
					Nos comprometemos a destinar tu donación a los programas de
					acompañamiento a las personas mayores.
				</Text>
				<ProtectedDonation
					style={{ marginBottom: 48 }}
					type="donation"
				/>
				<ProgressContainer>
					<PieChartCol>
						{renderProgressView(chartOptions[0], "right")}
					</PieChartCol>
					<PieChartCol>
						{renderProgressView(chartOptions[1], "bottom")}
						{renderProgressView(chartOptions[2])}
					</PieChartCol>
				</ProgressContainer>
				<Text
					type="p2"
					style={{
						padding: "12px 16px",
						backgroundColor: ColorV2.surface.background,
						color: ColorV2.text.neutralMedium,
						borderRadius: 8,
						marginTop: isMobile ? 8 : 32,
					}}
				>
					Somos una{" "}
					<span style={{ fontWeight: 600 }}>
						Fundación independiente
					</span>{" "}
					y no recibimos subvenciones. Nuestra actividad se sostiene
					gracias al apoyo de personas comprometidas con las personas
					mayores.
				</Text>
			</Section>
			{appLocation &&
				appLocation.country_code === "ES" &&
				appLocation.currency.name === "eur" && (
					<SectionBackground style={{ alignItems: "center" }}>
						<Text
							type="h2"
							weight="semibold"
							style={{ marginBottom: 24, textAlign: "center" }}
						>
							Recupera hasta el 80% de lo que dones
						</Text>
						<Text
							type="h6"
							style={{ marginBottom: 24, textAlign: "center" }}
						>
							Puedes deducirte tu donación en la Declaración de la
							Renta. De los primeros 250 € que dones te
							desgravarás el 80% y del resto el 40%*.
						</Text>
						<Text
							type="p2"
							style={{
								color: ColorV2.text.neutralMedium,
								textAlign: "center",
								marginBottom: 48,
							}}
						>
							Además, si colaboras con nosotros con una donación
							igual o superior durante al menos 3 años, la
							desgravación se incrementa hasta el 45%.
						</Text>
						<Text
							type="h5"
							weight="semibold"
							style={{ marginBottom: 18, textAlign: "center" }}
						>
							Te ponemos un ejemplo
						</Text>
						<PriceRow>
							<PriceView>
								<Icon
									src={require("../../../assets/images/icon/donation.webp")}
								/>
								<Col>
									<Text type="p" weight="medium">
										Si donas
									</Text>
									<Text type={"h2"} weight="semibold">
										{props.price}
										<span
											style={{
												fontSize: 18,
												fontWeight: 500,
											}}
										>
											,00€
										</span>
									</Text>
								</Col>
							</PriceView>
							<PriceView
								style={{
									backgroundColor: ColorV2.surface.secondary,
									borderRadius: 16,
								}}
							>
								<Icon
									src={require("../../../assets/images/icon/coins.webp")}
								/>
								<Col>
									<Text type="p" weight="medium">
										Te devuelven
									</Text>
									<Text type={"h2"} weight="semibold">
										{priceAmount.split(".")[0]}
										<span
											style={{
												fontSize: 18,
												fontWeight: 500,
											}}
										>
											,{priceAmount.split(".")[1]}€
										</span>
									</Text>
								</Col>
							</PriceView>
						</PriceRow>
						<Text
							type="c2"
							style={{
								color: ColorV2.text.neutralMedium,
								textAlign: "center",
							}}
						>
							*El límite de la deducción sobre la base liquidable
							se mantiene en el 10%. Para el País Vasco y Navarra
							consultar regímenes tributarios especiales.
						</Text>
					</SectionBackground>
				)}
			<Section>
				<Text type="h2" weight="semibold" style={{ marginBottom: 16 }}>
					Opiniones de quienes hacen esto posible
				</Text>
				<Row
					style={{
						alignItems: "center",
						gap: 8,
						marginBottom: isMobile ? 24 : 0,
					}}
				>
					<Text type="h4" weight="semibold">
						4,9
					</Text>
					<div>
						<Star
							fill={ColorV2.surface.secondary}
							color={ColorV2.surface.secondary}
						/>
						<Star
							fill={ColorV2.surface.secondary}
							color={ColorV2.surface.secondary}
						/>
						<Star
							fill={ColorV2.surface.secondary}
							color={ColorV2.surface.secondary}
						/>
						<Star
							fill={ColorV2.surface.secondary}
							color={ColorV2.surface.secondary}
						/>
						<Star
							fill={ColorV2.surface.secondary}
							color={ColorV2.surface.secondary}
						/>
					</div>
					<Text type="h4">+1.850 reseñas</Text>
				</Row>
				<TestimonyList options={testimonies} />
			</Section>
			<SectionBackground>
				<Col style={{ marginBottom: 48 }}>
					<Text
						type="h2"
						weight="semibold"
						style={{ marginBottom: 8, textAlign: "center" }}
					>
						Hablan de nosotros
					</Text>
					<Text type="h6" style={{ textAlign: "center" }}>
						+1.500 menciones en prensa
					</Text>
				</Col>
				<Col style={{ justifyContent: "space-between", gap: 24 }}>
					<PressRow>
						<PressLogo type="tve" />
						<PressLogo type="a3" />
						<PressLogo type="cuatro" />
						<PressLogo type="cope" />
					</PressRow>
					<PressRow>
						<PressLogo type="pais" />
						<PressLogo type="mundo" />
						<PressLogo type="forbes" />
						<PressLogo type="onda" />
					</PressRow>
				</Col>
			</SectionBackground>
			<Section
				style={{
					borderBottom: "1px solid " + ColorV2.border.neutralSoft,
				}}
			>
				<Text
					type="h2"
					weight="semibold"
					style={{ marginBottom: isMobile ? 8 : 24 }}
				>
					Nuestro impacto social en cifras
				</Text>
				<Text type="h6" style={{ marginBottom: isMobile ? 32 : 64 }}>
					Todo lo que hemos conseguido hasta ahora
				</Text>
				<Row style={{ gap: 48 }}>
					<Col style={{ display: isMobile ? "none" : "flex" }}>
						<DataImage
							src={require("../../../assets/images/history/juana.webp")}
						/>
					</Col>
					<Col
						style={{
							gap: 48,
							margin: isMobile ? "0px" : "48px 0px",
							justifyContent: "center"
						}}
					>
						{data.map((item, index) => (
							<div key={`data-${index}`}>
								<Text
									type="d1"
									weight="semibold"
									style={{
										color: ColorV2.text.primary,
										textAlign: isMobile ? "center" : "left",
									}}
								>
									{item.title}
								</Text>
								<Text
									type="h6"
									style={{
										textAlign: isMobile ? "center" : "left",
									}}
								>
									{item.subtitle}
								</Text>
							</div>
						))}
					</Col>
				</Row>
			</Section>
			<SectionFinal>
				<Text
					type="h2"
					style={{
						textAlign: "center",
					}}
				>
					Hemos acompañado a miles de personas mayores en situación de
					soledad no deseada,
				</Text>
				<Text
					type="h2"
					weight="semibold"
					style={{
						textAlign: "center",
						color: ColorV2.text.primary,
					}}
				>
					pero todavía nos quedan millones por acompañar.
				</Text>
			</SectionFinal>
		</Container>
	);
};
export default Info;
export interface Props {
	style?: CSSProperties;
	price: number;
	children?: React.ReactNode;
	onClick?: () => void;
}
